import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import React, { useEffect, useRef } from 'react';
import { View } from 'react-native';
import PortalManager from './portal-manager';
import { CustomStyleSheet } from '../../../../utils/rn/tools';
import EventEmitter from 'events';
export var PortalContext = /*#__PURE__*/React.createContext(null); // events

var addType = 'RN_ADD_PORTAL';
var removeType = 'RN_REMOVE_PORTAL'; // fix react native web does not support DeviceEventEmitter

var TopViewEventEmitter = new EventEmitter();
var PortalGuard = function PortalGuard() {
  var _this = this;
  _classCallCheck(this, PortalGuard);
  _defineProperty(this, "nextKey", 10000);
  _defineProperty(this, "add", function (e) {
    var key = _this.nextKey++;
    TopViewEventEmitter.emit(addType, e, key);
    return key;
  });
  _defineProperty(this, "remove", function (key) {
    return TopViewEventEmitter.emit(removeType, key);
  });
};
/**
 * portal
 */

export var portal = new PortalGuard();
/**
 * Portal host renders all of its children `Portal` elements.
 * For example, you can wrap a screen in `Portal.Host` to render items above the screen.
 * If you're using the `Provider` component, it already includes `Portal.Host`.
 *
 * ## Usage
 * ```js
 * import * as React from 'react';
 * import { Text } from 'react-native';
 * import { Portal } from '@ant-design/react-native';
 *
 * export default class MyComponent extends React.Component {
 *   render() {
 *     return (
 *       <Portal.Host>
 *         <Text>Content of the app</Text>
 *       </Portal.Host>
 *     );
 *   }
 * }
 * ```
 *
 * Here any `Portal` elements under `<App />` are rendered alongside `<App />` and will appear above `<App />` like a `Modal`.
 */

export default function PortalHost(props) {
  var children = props.children,
    containerStyle = props.containerStyle;
  var _nextKey = useRef(0);
  var _queue = useRef([]);
  var _manager = useRef(null);
  useEffect(function () {
    var manager = _manager.current;
    var queue = _queue.current;
    TopViewEventEmitter.addListener(addType, _mount);
    TopViewEventEmitter.addListener(removeType, _unmount);
    while (queue.length && manager) {
      var action = queue.pop();
      if (!action) {
        continue;
      } // tslint:disable-next-line:switch-default

      switch (action.type) {
        case 'mount':
          manager.mount(action.key, action.children);
          break;
        case 'update':
          manager.update(action.key, action.children);
          break;
        case 'unmount':
          manager.unmount(action.key);
          break;
      }
    }
    return function () {
      TopViewEventEmitter.removeAllListeners();
    };
  }, []);
  var _mount = function _mount(children, _key) {
    var key = _key || _nextKey.current++;
    if (_manager.current) {
      _manager.current.mount(key, children);
    } else {
      _queue.current.push({
        type: 'mount',
        key: key,
        children: children
      });
    }
    return key;
  };
  var _update = function _update(key, children) {
    if (_manager.current) {
      _manager.current.update(key, children);
    } else {
      var op = {
        type: 'mount',
        key: key,
        children: children
      };
      var index = _queue.current.findIndex(function (o) {
        return o.type === 'mount' || o.type === 'update' && o.key === key;
      });
      if (index > -1) {
        _queue.current[index] = op;
      } else {
        _queue.current.push(op);
      }
    }
  };
  var _unmount = function _unmount(key) {
    if (_manager.current) {
      _manager.current.unmount(key);
    } else {
      _queue.current.push({
        type: 'unmount',
        key: key
      });
    }
  };
  return /*#__PURE__*/React.createElement(PortalContext.Provider, {
    value: {
      mount: _mount,
      update: _update,
      unmount: _unmount
    }
  }, /*#__PURE__*/React.createElement(View, {
    style: [styles.container, containerStyle],
    collapsable: false
  }, children), /*#__PURE__*/React.createElement(PortalManager, {
    ref: _manager
  }));
}
PortalHost.displayName = 'Portal.Host';
var styles = CustomStyleSheet({
  container: {
    flex: 1
  }
});